/**
 * Widget Adapter Service
 *
 * Anti-corruption layer between provider configuration domain and widget domain.
 * Transforms backend provider DTOs into widget-specific types.
 *
 * This service isolates the widget from changes to provider configuration DTOs,
 * ensuring loose coupling between domains.
 */

import type { AdditionalService } from '../types/provider.types';
import type { WidgetService } from '../types/order-builder';

/**
 * Transform provider additional services to widget service format
 *
 * Filters to only enabled services and maps to simplified widget structure.
 * This transformation ensures the widget only deals with the data it needs,
 * hiding provider-specific fields like category, timestamps, etc.
 *
 * @param services - Provider's additional services (may be undefined)
 * @returns Array of widget-compatible services
 */
export function adaptServicesToWidget(
  services: AdditionalService[] | undefined
): WidgetService[] {
  if (!services) return [];

  return services
    .filter(service => service.enabled)
    .map(service => ({
      id: service.id,
      name: service.name,
      description: service.description,
      basePrice: service.priceCents,
    }));
}
